import React, { useState, useEffect } from 'react';
import { AlertTriangle, Loader, Trash2 } from 'lucide-react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassModal } from '../ui/GlassModal';
import { smsApi, SmsOptOutItem } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const getApiError = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    return fallback;
};

export default function SmsOptOutsPanel() {
    const { user } = useAuth();
    // IMPR-009: the backend 404s a non-admin trying to delete a global
    // (owner_id IS NULL) opt-out row — only admin/superadmin can act on
    // those (sms_routes.delete_opt_out). Match that on the frontend so the
    // button doesn't render as clickable-but-broken for everyone else.
    const canActOnGlobalRows = user?.role === 'admin' || user?.role === 'superadmin';
    const [items, setItems] = useState<SmsOptOutItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [newPhone, setNewPhone] = useState('');
    const [newReason, setNewReason] = useState('');
    const [adding, setAdding] = useState(false);
    const [confirmTarget, setConfirmTarget] = useState<SmsOptOutItem | null>(null);

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await smsApi.listOptOuts();
            setItems(res.items);
        } catch (e: any) {
            setError(getApiError(e, 'Failed to load opt-outs'));
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (!newPhone) return;
        setAdding(true);
        setError(null);
        try {
            await smsApi.createOptOut({ phone: newPhone, reason: newReason || undefined });
            setNewPhone('');
            setNewReason('');
            await load();
        } catch (e: any) {
            setError(getApiError(e, 'Failed to add opt-out'));
        } finally {
            setAdding(false);
        }
    };

    const handleReOptIn = async () => {
        if (!confirmTarget) return;
        try {
            await smsApi.deleteOptOut(confirmTarget.opt_out_id);
            setConfirmTarget(null);
            await load();
        } catch (e: any) {
            setError(getApiError(e, 'Failed to re-opt-in'));
        }
    };

    return (
        <GlassCard>
            <div className="sending-form">
                <h4 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>SMS opt-outs</h4>
                <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
                    Numbers that have replied STOP, been manually suppressed, or been blocked by a provider's own unsubscribe list.
                </p>

                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)' }}>
                        <Loader size={14} className="spin" /> Loading…
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--stroke)', textAlign: 'left', color: 'var(--text-secondary)' }}>
                                    <th style={{ padding: '6px 8px' }}>Phone</th>
                                    <th style={{ padding: '6px 8px' }}>Source</th>
                                    <th style={{ padding: '6px 8px' }}>Keyword</th>
                                    <th style={{ padding: '6px 8px' }}>Date</th>
                                    <th style={{ padding: '6px 8px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 && (
                                    <tr><td colSpan={5} style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>No opt-outs recorded.</td></tr>
                                )}
                                {items.map(item => (
                                    <tr key={item.opt_out_id} style={{ borderBottom: '1px solid var(--stroke)' }}>
                                        <td style={{ padding: '6px 8px', color: 'var(--text)' }}>
                                            {item.phone_e164} {item.is_global && <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>(global)</span>}
                                        </td>
                                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{item.source}</td>
                                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{item.keyword || '—'}</td>
                                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>
                                            {item.opted_out_at ? new Date(item.opted_out_at).toLocaleDateString() : '—'}
                                        </td>
                                        <td style={{ padding: '6px 8px' }}>
                                            {(!item.is_global || canActOnGlobalRows) ? (
                                                <GlassButton variant="danger" onClick={() => setConfirmTarget(item)}>
                                                    <Trash2 size={12} /> Re-opt-in
                                                </GlassButton>
                                            ) : (
                                                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }} title="Only an admin can re-opt-in a platform-wide suppression">
                                                    Admin only
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {error && (
                    <div className="sending-error" style={{ marginTop: 8 }}>
                        <AlertTriangle size={14} /> {error}
                    </div>
                )}

                <div className="sending-row" style={{ marginTop: 12 }}>
                    <div className="sending-field">
                        <label className="sending-label">Phone</label>
                        <input
                            className="sending-input"
                            type="text"
                            value={newPhone}
                            placeholder="0412 345 678"
                            onChange={e => setNewPhone(e.target.value)}
                        />
                    </div>
                    <div className="sending-field">
                        <label className="sending-label">Reason (optional)</label>
                        <input
                            className="sending-input"
                            type="text"
                            value={newReason}
                            placeholder="Phoned in"
                            onChange={e => setNewReason(e.target.value)}
                        />
                    </div>
                </div>
                <div className="sending-actions">
                    <GlassButton variant="secondary" onClick={handleAdd} disabled={adding || !newPhone}>
                        {adding ? <Loader size={14} className="spin" /> : null}
                        Add opt-out
                    </GlassButton>
                </div>
            </div>

            <GlassModal
                isOpen={!!confirmTarget}
                onClose={() => setConfirmTarget(null)}
                title="Remove this opt-out?"
                footer={
                    <>
                        <GlassButton variant="secondary" onClick={() => setConfirmTarget(null)}>Cancel</GlassButton>
                        <GlassButton variant="danger" onClick={handleReOptIn}>Confirm</GlassButton>
                    </>
                }
            >
                <p>Removing this record means AIME will send to this number again. Only do this if you have fresh, documented consent.</p>
            </GlassModal>
        </GlassCard>
    );
}
